import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Popconfirm, Select, Space, Spin, Switch, Table, Tag, Typography, message,
} from 'antd';
import { ReloadOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons';
import {
  createRowActionConfig,
  deleteRowActionConfig,
  getActionConfigModules,
  getRowActionConfigs,
  updateRowActionConfig,
} from '../../../../services/adminApi';
import { normalizeModuleOptions } from '../../../../utils/moduleOptions';

const { Text, Title } = Typography;

// Composite key for a child action so two groups can share a child key.
const childId = (parentKey, key) => `${parentKey}::${key}`;

// Mirrors the backend's detailVariantBase (models/rowActionConfig.go): a
// module's DETAIL surface can be served under its own config key, and a role
// override authored on the base module now also applies there (subtractively).
// Used only to TELL the admin how far this override reaches — the resolution
// itself is entirely backend-side.
const norm = (value) => String(value ?? '').trim().toLowerCase();
function detailVariantBase(module) {
  const key = norm(module);
  for (const suffix of ['details', 'detail']) {
    if (key.endsWith(suffix)) {
      const base = key.slice(0, -suffix.length);
      if (base) return base;
    }
  }
  return '';
}
// Two keys are the same module family if one is a detail variant of the other,
// tolerating the singular/plural spellings the backend's aliases also accept.
function sameFamily(a, b) {
  const [x, y] = [norm(a), norm(b)];
  const singular = (v) => v.replace(/s$/, '');
  return singular(detailVariantBase(x)) === singular(y)
    || singular(detailVariantBase(y)) === singular(x);
}

function enabledSetFromActions(actions) {
  const set = new Set();
  (actions ?? []).forEach((a) => {
    set.add(a.key);
    (a.children ?? []).forEach((c) => set.add(childId(a.key, c.key)));
  });
  return set;
}

/**
 * Per-role row-action configuration, embedded in the "Configure User Type"
 * modal (Admin → User Types → Config → Actions).
 *
 * The action LIBRARY (what each action does) lives in the roleId-0 default
 * config, managed in Configurations → Actions. This editor only decides which
 * of those actions a specific role gets: saving writes a roleId-scoped
 * rowActionConfig containing the enabled subset — the same doc the runtime
 * (models.GetRowActions) already prefers over the roleId-0 fallback.
 */
export default function RoleActionsEditor({ role }) {
  const roleId = Number(role?.roleId ?? 0);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(new Set());
  const [dirty, setDirty] = useState(false);
  const [allModuleKeys, setAllModuleKeys] = useState([]);

  // Sibling surfaces this override also reaches (see sameFamily).
  const familyKeys = useMemo(
    () => allModuleKeys.filter((key) => sameFamily(key, selectedModule)),
    [allModuleKeys, selectedModule],
  );

  useEffect(() => {
    getActionConfigModules()
      .then((mods) => {
        const unique = normalizeModuleOptions(mods).map((m) => m.value);
        setModules(unique);
        setSelectedModule((current) => current || unique[0] || '');
      })
      .catch(() => setModules([]));
    // Every configured module key, used only to show which sibling surfaces
    // (e.g. a detail-view config) this role override will also cover.
    getRowActionConfigs()
      .then((all) => setAllModuleKeys([...new Set(
        (Array.isArray(all) ? all : []).map((c) => String(c?.module ?? '').trim()).filter(Boolean),
      )]))
      .catch(() => setAllModuleKeys([]));
  }, []);

  const loadConfigs = useCallback(async () => {
    if (!selectedModule) return;
    setLoading(true);
    try {
      const data = await getRowActionConfigs(selectedModule);
      setConfigs(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e.message);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [selectedModule]);

  useEffect(() => { queueMicrotask(() => { loadConfigs(); }); }, [loadConfigs]);

  // Prefer the exact module key over alias variants (job/jobs), same as
  // RowActionsSection, so we never edit a legacy duplicate.
  const pickConfig = useCallback((rid) => {
    const forRole = configs.filter((c) => c.roleId === rid);
    return forRole.find((c) => c.module === selectedModule) ?? forRole[0] ?? null;
  }, [configs, selectedModule]);

  const baseConfig = useMemo(() => pickConfig(0), [pickConfig]);
  const roleConfig = useMemo(() => pickConfig(roleId), [pickConfig, roleId]);

  // The library shown is the default (All Roles) config; if only a role
  // config exists, fall back to it so its actions stay visible/editable.
  const libraryActions = useMemo(
    () => baseConfig?.rowActions ?? roleConfig?.rowActions ?? [],
    [baseConfig, roleConfig],
  );

  // No role override yet → the role inherits everything from the default.
  useEffect(() => {
    queueMicrotask(() => {
      setEnabled(enabledSetFromActions(roleConfig ? roleConfig.rowActions : libraryActions));
      setDirty(false);
    });
  }, [roleConfig, libraryActions]);

  function toggle(id, on, childIds = []) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (on) {
        next.add(id);
        childIds.forEach((c) => next.add(c));
      } else {
        next.delete(id);
        childIds.forEach((c) => next.delete(c));
      }
      return next;
    });
    setDirty(true);
  }

  async function handleSave() {
    const rowActions = libraryActions
      .filter((a) => enabled.has(a.key))
      .map((a) => (a.children?.length
        ? { ...a, children: a.children.filter((c) => enabled.has(childId(a.key, c.key))) }
        : a));
    setSaving(true);
    try {
      if (roleConfig) {
        await updateRowActionConfig(roleConfig.id ?? roleConfig._id, rowActions);
      } else {
        await createRowActionConfig({ module: selectedModule, roleId, rowActions });
      }
      message.success(`Actions saved for ${role?.roleName ?? 'role'}`);
      await loadConfigs();
    } catch (e) {
      message.error(e.message);
      if (String(e.message ?? '').toLowerCase().includes('already exists')) loadConfigs();
    } finally {
      setSaving(false);
    }
  }

  async function handleResetToDefault() {
    if (!roleConfig) return;
    setSaving(true);
    try {
      await deleteRowActionConfig(roleConfig.id ?? roleConfig._id);
      message.success('Role override removed — this role now inherits the default actions');
      await loadConfigs();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  const columns = [
    { title: 'Action', key: 'identity', render: (_, a) => (
      <Space direction="vertical" size={0}>
        <Text strong>{a.label || a.key}</Text>
        <Text type="secondary" code>{a.key}</Text>
      </Space>
    ) },
    { title: 'Behaviour', key: 'behaviour', width: 220, render: (_, a) => (
      <Space direction="vertical" size={0}>
        <Tag color="blue" style={{ width: 'fit-content' }}>{a.actionType}</Tag>
        <Text type="secondary" ellipsis={{ tooltip: true }} style={{ maxWidth: 190 }}>
          {a.actionType === 'group'
            ? `${(a.children ?? []).length} child action(s)`
            : (a.popupKey || a.routeTemplate || a.apiPath || a.copyField || a.documentField || '—')}
        </Text>
      </Space>
    ) },
    { title: 'Enabled for this role', key: 'enabled', width: 160, render: (_, a) => (
      <Switch
        checked={enabled.has(a.key)}
        onChange={(on) => toggle(a.key, on, (a.children ?? []).map((c) => childId(a.key, c.key)))}
      />
    ) },
  ];

  const childColumns = (parent) => [
    { title: 'Child Action', key: 'identity', render: (_, c) => (
      <Space direction="vertical" size={0}>
        <Text strong>{c.label || c.key}</Text>
        <Text type="secondary" code>{c.key}</Text>
      </Space>
    ) },
    { title: 'Behaviour', key: 'behaviour', width: 220, render: (_, c) => (
      <Text type="secondary">{c.popupKey || c.routeTemplate || c.apiPath || c.actionType}</Text>
    ) },
    { title: 'Enabled', key: 'enabled', width: 160, render: (_, c) => (
      <Switch
        disabled={!enabled.has(parent.key)}
        checked={enabled.has(childId(parent.key, c.key))}
        onChange={(on) => toggle(childId(parent.key, c.key), on)}
      />
    ) },
  ];

  return (
    <section className="admin-editor">
      <div className="admin-section-head">
        <div>
          <Title level={4}>Role Actions</Title>
          <Text type="secondary">
            Enable or disable row actions for {role?.roleName ?? 'this role'}. The action
            library itself is managed in Configurations → Actions.
          </Text>
        </div>
        <Space>
          {roleConfig && (
            <Popconfirm
              title="Remove this role's override and inherit the default actions?"
              okText="Reset"
              okType="danger"
              onConfirm={handleResetToDefault}
            >
              <Button icon={<UndoOutlined />} loading={saving}>Reset to Default</Button>
            </Popconfirm>
          )}
          <Button icon={<ReloadOutlined />} onClick={loadConfigs} loading={loading}>Reload</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!dirty || !selectedModule}
            onClick={handleSave}
          >
            Save
          </Button>
        </Space>
      </div>

      <Space style={{ marginBottom: 12 }} wrap>
        <label>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Module</Text>
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
            mode="tags"
            maxCount={1}
            value={selectedModule ? [selectedModule] : []}
            placeholder="Pick or type a module key"
            onChange={(v) => setSelectedModule(String(v[v.length - 1] ?? '').trim())}
            options={modules.map((m) => ({ value: m, label: m }))}
          />
        </label>
        <div style={{ paddingTop: 20 }}>
          {roleConfig
            ? <Tag color="green">Role override active</Tag>
            : <Tag color="orange">Inheriting default (All Roles) — Save creates an override</Tag>}
          {dirty && <Text type="warning" style={{ marginLeft: 8 }}>Unsaved changes</Text>}
        </div>
      </Space>

      {familyKeys.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`This override also applies to: ${familyKeys.join(', ')}`}
          description={
            'Those surfaces (e.g. the detail view) keep their own extra actions — only the '
            + 'ones you turn off here are hidden from them.'
          }
        />
      )}

      {!loading && !libraryActions.length && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="No actions configured for this module yet."
          description="Define the actions first in Configurations → Actions (Action library), then enable them per role here."
        />
      )}

      <Spin spinning={loading}>
        <Table
          dataSource={libraryActions.map((a, i) => ({ ...a, _rowKey: a.key || i }))}
          rowKey="_rowKey"
          columns={columns}
          pagination={false}
          size="small"
          expandable={{
            rowExpandable: (r) => r.actionType === 'group' && (r.children ?? []).length > 0,
            expandedRowRender: (r) => (
              <Table
                dataSource={(r.children ?? []).map((c, i) => ({ ...c, _rowKey: c.key || i }))}
                rowKey="_rowKey"
                columns={childColumns(r)}
                pagination={false}
                size="small"
              />
            ),
          }}
        />
      </Spin>
    </section>
  );
}
